import ApiClient from 'api-client/ApiClient'
import { getActiveRegion, getActiveTenant, getDomainName } from 'api-client/helpers'
import { groupByRegion } from 'api-client/Keystone'
import { getClusterEndpoint } from '../clusters/action-helpers'
import { getInfraRegionURL } from '../secrets/actions'
import { isAirgapped, isDevelopment, isLocalhost } from 'core/utils/helpers'

interface HostedControlPlaneParams {
  name: string
  namespace: string
  version: string
  apiServerFlags?: string[]
  schedulerFlags?: string[]
  controllerManagerFlags?: string[]
  clusterName: string
}

export const getRegionURLs = () => {
  const { serviceCatalog } = ApiClient.getInstance()
  const servicesByRegion = groupByRegion(serviceCatalog)
  const regionKeys = Object.keys(servicesByRegion)

  return regionKeys.reduce((acc, region) => {
    const resmgrEndpoint = servicesByRegion?.[region]?.resmgr?.public?.url
    const baseUrl = resmgrEndpoint?.split('/resmgr')?.[0]
    return baseUrl ? (acc ? `${acc},${baseUrl}` : baseUrl) : acc
  }, '')
}

export const createHostedControlPlaneBody = ({
  name,
  namespace,
  version,
  apiServerFlags = [],
  schedulerFlags = [],
  controllerManagerFlags = [],
  clusterName,
}: HostedControlPlaneParams) => {
  const regionURLs = getRegionURLs()
  // ClusterEndpoint
  const clusterEndpoint = getClusterEndpoint({
    clusterName,
  })

  // ControlPlaneEndpoint
  const clusterCPEndpoint = getClusterEndpoint({
    clusterName: name,
  })

  const infraRegionURL = getInfraRegionURL() || ''

  // Get DU Shortname
  const duShortName = infraRegionURL
    .split('.')[0]
    .split('/')
    .pop()
    .toLowerCase()

  // Get DU Subdomain
  const duSubDomain = infraRegionURL
    .split('.')
    .slice(1)
    .join('.')
    .toLowerCase()

  return {
    apiVersion: 'controlplane.platform9.io/v1alpha1',
    kind: 'HostedControlPlane',
    metadata: {
      name,
      namespace,
      labels: {
        'pcd-kaapi.pf9.io/region': getActiveRegion(),
      },
    },
    spec: {
      du: {
        name: duShortName,
        region: getActiveRegion().toLowerCase(),
        keystoneDomain: getDomainName().toLowerCase(),
        keystoneTenant: getActiveTenant().toLowerCase(),
        domain: duSubDomain,
      },
      apiServer: {
        extraArgs: [
          ...apiServerFlags,
          '--advertise-address=10.96.0.40',
          `--cors-allowed-origins=${regionURLs}${
            isDevelopment() || isLocalhost() ? ',http://localhost:3000' : ''
          }`,
        ],
      },
      scheduler: {
        extraArgs: schedulerFlags,
      },
      controllerManager: {
        extraArgs: [...controllerManagerFlags, '--cloud-provider=external'],
      },
      addons: {
        coreDNS: {},
        konnectivity: {
          server: {
            port: 8132,
          },
          agent: {
            hostNetwork: true,
            extraArgs: [
              `--proxy-server-host=konnectivity-${clusterEndpoint}`,
              '--proxy-server-port=443',
            ],
          },
        },
      },
      extraCertSANs: [
        `konnectivity-${clusterEndpoint}`,
        `${clusterEndpoint}`,
        `${clusterCPEndpoint}`,
      ],
      kubelet: {
        preferredAddressTypes: ['InternalIP', 'ExternalIP', 'Hostname'],
        cgroupfs: 'systemd',
      },
      version,
      hcpClass: 'default',
      hostname: `${clusterEndpoint}`,
    },
  }
}
